import { Box, Container, Flex, Grid, Heading, Image, SystemStyleObject } from '@chakra-ui/react';
import {
  AppLink,
  Badge,
  BadgeColor,
  Card,
  GradientShadowOverlay,
  LANDING_ROUTES,
  pxToRem,
} from '@cyberkongz/ui';
import { SliderWrapper } from '@cyberkongz/ui-slider';
import { ReactNode } from 'react';
import { SwiperSlide } from 'swiper/react';

type CardsItemsBase = {
  image: string;
  title: string;
  description: ReactNode;
  imageStyles?: SystemStyleObject;
};

export function CardsSliderSection({
  cards,
  sxImage,
}: {
  cards: CardsItemsBase[];
  sxImage?: SystemStyleObject;
}) {
  return (
    <Box pt="20" pb={[10, 16, pxToRem(100)]} position="relative">
      <GradientShadowOverlay top height={pxToRem(290)} />
      <Container position="relative" zIndex="2">
        <Heading size="sm" color="neutral.100">
          FEATURES
        </Heading>
        <SliderWrapper
          css={{ mt: 8, '& .swiper-slide': { display: 'flex', height: 'auto' } }}
          slidesPerView={1.5}
          spaceBetween={10}
          breakpoints={{
            768: {
              slidesPerView: 2.5,
              spaceBetween: 20,
            },
            1024: {
              slidesPerView: 4,
              spaceBetween: 20,
            },
          }}
        >
          {cards.map(({ image, title, description, imageStyles }) => (
            <SwiperSlide key={title}>
              <Card variant="landing" key={title}>
                <Flex css={{ height: pxToRem(100), alignItems: 'center' }}>
                  <Image
                    src={`/static/icons/${image}`}
                    alt={`${title} image`}
                    css={{ minHeight: pxToRem(60), ...sxImage, ...imageStyles }}
                  />
                </Flex>
                {/* TO DO TEXT VARIANT */}
                <Heading
                  css={{
                    letterSpacing: '0.03em',
                    mt: 4,
                    mb: 1,
                    fontSize: ['sm', 'md'],
                  }}
                >
                  {title}
                </Heading>
                <Box
                  css={{
                    letterSpacing: '0.01em',
                    '& a': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {description}
                </Box>
              </Card>
            </SwiperSlide>
          ))}
        </SliderWrapper>
      </Container>
    </Box>
  );
}

export type CardsVerticalItemsType = (CardsItemsBase & {
  badgeColor?: BadgeColor;
  badgeLabel?: string;
})[];

export function CardsVerticalSection({ cards }: { cards: CardsVerticalItemsType }) {
  return (
    <Box pb={[20, pxToRem(120)]} position="relative">
      <Container position="relative" zIndex="2">
        <Heading size="sm" color="neutral.100">
          Use-Cases
        </Heading>
        <Grid gap={[4, 6]} mt={[4, 8]} maxW={pxToRem(712)}>
          {cards.map(({ badgeColor: stage, badgeLabel: stageLabel, image, title, description }) => (
            <Card variant="landing" key={title}>
              {stage && stageLabel ? (
                <Badge css={{ mb: 4 }} badgeColor={stage} withDot>
                  {stageLabel}
                </Badge>
              ) : null}
              <Flex css={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                  {/* TO DO TEXT VARIANT */}
                  <Heading
                    css={{
                      letterSpacing: '0.03em',
                      mb: 2,
                      fontSize: ['sm', 'lg'],
                    }}
                  >
                    {title}
                  </Heading>
                  <Box
                    css={{ letterSpacing: '0.01em', pr: 2, '& a': { textDecoration: 'underline' } }}
                  >
                    {description}
                  </Box>
                </Box>
                <Image
                  w={[pxToRem(30), pxToRem(50)]}
                  src={`/static/icons/${image}`}
                  alt={`${title} image`}
                />
              </Flex>
            </Card>
          ))}
        </Grid>
      </Container>
      <GradientShadowOverlay height={pxToRem(290)} />
    </Box>
  );
}

export const COMMON_UTILITIES = {
  onchain_genes: (type: string) => ({
    image: `onchain_genes_${type.toLowerCase()}.png`,
    title: 'Onchain Genes & Art',
    description: (
      <Box>
        Each {type} has their traits and art saved{' '}
        <AppLink href="https://0xinuarashi.medium.com/introducing-on-chain-kongz-ac82f68d15a">
          on-chain.
        </AppLink>
      </Box>
    ),
  }),
};

export const COMMON_USE_CASES = {
  vote: {
    image: 'vote.png',
    title: '!OOH Governance',
    description: (
      <Box>
        Part of the experimental governance system called{' '}
        <AppLink
          href={LANDING_ROUTES.BLOG_POST(
            'cyberkongz-ooh-a-first-iteration-of-community-governance'
          )}
        >
          !OOH.
        </AppLink>
      </Box>
    ),
  },
  merch: {
    image: 'merch.png',
    title: 'Exclusive Events & Merch',
    description: (
      <Box>
        Token-Gated access to exclusive{' '}
        <AppLink href={LANDING_ROUTES.BLUEPRINT_TAB('irl')}>IRL Events & Merch.</AppLink>
      </Box>
    ),
  },
  customize: {
    image: 'customize.png',
    title: 'Customization',
    description: 'Make your CyberKong yours with custom name and bio.',
  },
};
